import React, { useState, useEffect, useRef } from "react";
// import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Alert } from "../../../components/ui/alert";
import { ChevronUp, ChevronDown, X } from "lucide-react";
import { useSystemTheme } from "../hooks/useSystemTheme";
import "../../sales-forecast/styles/theme.css";

const AMENITY_OPTIONS = [
  "Basketball", "Clubhouse", "Gated", "Large Lots", "Pickleball",
  "Playground", "Pond", "Pool", "Prime Location", "Splash Pad",
  "Tennis", "Walking Trails",
];

export interface AmenityControlProps {
  selectedAmenities: string[];
  onChange: (selected: string[]) => void;
  clusterName: string;
  clusterDescription: string;
  serverSpecPct?: number | null;
  serverPresoldPct?: number | null;
}


export const AmenityControl: React.FC<AmenityControlProps> = ({
  selectedAmenities,
  onChange,
  clusterName,
  clusterDescription,
  serverSpecPct,
  serverPresoldPct,
}) => {
  const theme = useSystemTheme();
  const [error, setError] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setError("");
  }, [selectedAmenities]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const toggle = (opt: string) => {
    const next = selectedAmenities.includes(opt)
      ? selectedAmenities.filter((a) => a !== opt)
      : [...selectedAmenities, opt];
    setError("");
    onChange(next);
  };
  
  const remove = (opt: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selectedAmenities.filter((a) => a !== opt));
  };

  return (
    <div ref={containerRef} className={`amenity-control theme-${theme}`} style={{ position: "relative" }}>
      {/* Section label */}
      <div
        className="flex items-center gap-1 mb-2"
        style={{ color: "var(--muted-text-color)" }}
      >
        <span className="text-xs font-semibold tracking-widest uppercase">
          Amenities
        </span>
        {selectedAmenities.length > 0 && (
          <span className="text-xs font-semibold tracking-widest uppercase">
            · {selectedAmenities.length} Selected
          </span>
        )}
      </div>

      {/* Trigger row */}
      <div
        style={{
          border: isOpen ? "1px solid var(--accent-color)" : "1px solid var(--border-color)",
          borderBottom: isOpen ? "none" : "1px solid var(--border-color)",
          borderRadius: isOpen ? "8px 8px 0 0" : "8px",
        }}
      >
        {/* Chips row */}
        <div
          className="flex items-center justify-between gap-2 px-3 py-2 cursor-pointer"
          style={{
            backgroundColor: "var(--input-bg)",
            minHeight: "2.5rem",
            borderRadius: isOpen ? "8px 8px 0 0" : "8px",
          }}
          onClick={() => setIsOpen((v) => !v)}
        >
          <div className="flex flex-wrap gap-2 flex-1">
            {selectedAmenities.length === 0 ? (
              <span
                className="text-sm"
                style={{ color: "var(--muted-text-color)" }}
              >
                Select amenities…
              </span>
            ) : (
              selectedAmenities.map((opt) => (
                <span
                  key={opt}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-md text-sm font-medium"
                  style={{
                    backgroundColor: "var(--subcard-bg)",
                    border: "1px solid var(--border-color)",
                    color: "var(--text-color)",
                  }}
                >
                  {opt}
                  <X
                    size={12}
                    className="cursor-pointer opacity-70 hover:opacity-100"
                    onClick={(e) => remove(opt, e)}
                  />
                </span>
              ))
            )}
          </div>
          {isOpen ? (
            <ChevronUp size={16} style={{ color: "var(--muted-text-color)", flexShrink: 0 }} />
          ) : (
            <ChevronDown size={16} style={{ color: "var(--muted-text-color)", flexShrink: 0 }} />
          )}
        </div>

      </div>

      {/* Dropdown checkbox grid — overlays content below, anchored to root */}
      {isOpen && (
        <div
          className="p-3"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 50,
            backgroundColor: "var(--input-bg)",
            border: "1px solid var(--accent-color)",
            borderTop: "none",
            borderRadius: "0 0 8px 8px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
          }}
        >
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            {AMENITY_OPTIONS.map((opt) => {
              const checked = selectedAmenities.includes(opt);
              return (
                <div
                  key={opt}
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={() => toggle(opt)}
                >
                  <div
                    className="flex items-center justify-center rounded"
                    style={{
                      width: "1.1rem",
                      height: "1.1rem",
                      flexShrink: 0,
                      backgroundColor: checked ? "var(--accent-color)" : "transparent",
                      border: checked
                        ? "1.5px solid var(--accent-color)"
                        : "1.5px solid var(--muted-text-color)",
                    }}
                  >
                    {checked && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path
                          d="M1 4L3.5 6.5L9 1"
                          stroke="#1A2229"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  <Label
                    className="cursor-pointer text-sm"
                    style={{ color: "var(--text-color)", userSelect: "none" }}
                  >
                    {opt}
                  </Label>
                </div>
              );
            })}
          </div>
        </div>
      )}
      	
      {error && (
        <Alert
          className="mt-2 rounded-md"
          style={{
            backgroundColor: "var(--error-bg)",
            color: "var(--error-text)",
            border: "1px solid var(--border-color)",
          }}
        >
          {error}
        </Alert>
      )}
    </div>
  );
};
